import { Button } from '@/components/ui';
import { WelcomeFireworks } from '@/pages/professional/components/WelcomeFireworks';
import { ArrowRight, CheckCircle2, Info } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getUserData } from '@/utils/cookies';
import { Emoji } from 'emoji-picker-react';
import { useEffect, useState } from 'react';
import {
	clearUserFirstAccess,
	hasUserFirstAccess,
	hasUserSignupContext,
} from '@/utils';
import { useGetUser } from '@/services';

const PLUMAA_LOGO_URL =
	'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1770986601/Plumaa_H_psjyrs.png';

const UserFirstAccess = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const canAccess = hasUserFirstAccess() && hasUserSignupContext();
	const { data: user, isLoading } = useGetUser(
		{ isMe: true },
		{ refetchOnWindowFocus: false },
	);
	const displayName =
		user?.first_name?.trim() || getUserData()?.first_name?.trim() || 'Você';
	const [stage, setStage] = useState<1 | 2 | 3>(1);
	const [firstScreenEntered, setFirstScreenEntered] = useState(false);
	const [showContinueButton, setShowContinueButton] = useState(false);
	const [secondScreenEntered, setSecondScreenEntered] = useState(false);
	const [showAdvanceButton, setShowAdvanceButton] = useState(false);
	const [thirdScreenEntered, setThirdScreenEntered] = useState(false);
	const [showStartButton, setShowStartButton] = useState(false);

	useEffect(() => {
		window.scrollTo({ top: 0 });
		document
			.querySelector('[class*="overflow-y-scroll"]')
			?.scrollTo({ top: 0 });

		if (stage === 1) {
			setFirstScreenEntered(false);
			setShowContinueButton(false);

			let innerRaf = 0;
			const outerRaf = window.requestAnimationFrame(() => {
				innerRaf = window.requestAnimationFrame(() =>
					setFirstScreenEntered(true),
				);
			});

			const continueId = window.setTimeout(() => {
				setShowContinueButton(true);
			}, 2000);

			return () => {
				window.cancelAnimationFrame(outerRaf);
				if (innerRaf) window.cancelAnimationFrame(innerRaf);
				window.clearTimeout(continueId);
			};
		}

		if (stage === 2) {
			setSecondScreenEntered(false);
			setShowAdvanceButton(false);

			let innerRaf2 = 0;
			const outerRaf2 = window.requestAnimationFrame(() => {
				innerRaf2 = window.requestAnimationFrame(() =>
					setSecondScreenEntered(true),
				);
			});

			const advanceId = window.setTimeout(() => {
				setShowAdvanceButton(true);
			}, 1000);

			return () => {
				window.cancelAnimationFrame(outerRaf2);
				if (innerRaf2) window.cancelAnimationFrame(innerRaf2);
				window.clearTimeout(advanceId);
			};
		}

		if (stage === 3) {
			setThirdScreenEntered(false);
			setShowStartButton(false);

			let innerRaf3 = 0;
			const outerRaf3 = window.requestAnimationFrame(() => {
				innerRaf3 = window.requestAnimationFrame(() =>
					setThirdScreenEntered(true),
				);
			});

			const startId = window.setTimeout(() => {
				setShowStartButton(true);
			}, 1000);

			return () => {
				window.cancelAnimationFrame(outerRaf3);
				if (innerRaf3) window.cancelAnimationFrame(innerRaf3);
				window.clearTimeout(startId);
			};
		}
	}, [stage]);

	const handleStartForm = () => {
		clearUserFirstAccess();
		navigate('/formulario');
	};

	if (isLoading) {
		return null;
	}

	if (!canAccess || !user?.employee) {
		if (canAccess) clearUserFirstAccess();
		return <Navigate to="/home" replace state={location.state} />;
	}

	return (
		<section
			className={`relative flex min-h-full w-full items-start justify-center px-3 py-4 transition-[background-color] duration-700 ease-out md:items-center md:px-5 md:py-6`}
		>
			<Helmet>
				<title>Boas-vindas | Plumaa</title>
			</Helmet>

			{stage === 1 && <WelcomeFireworks />}

			<div
				key={stage}
				className={`relative z-0 mx-auto min-h-full w-full max-w-[1120px] transition-all duration-700 ease-out ${
					stage === 1
						? firstScreenEntered
							? 'translate-y-0 opacity-100'
							: 'translate-y-6 opacity-0'
						: stage === 2
						  ? secondScreenEntered
								? 'translate-y-0 opacity-100'
								: 'translate-y-6 opacity-0'
						  : stage === 3
						    ? thirdScreenEntered
									? 'translate-y-0 opacity-100'
									: 'translate-y-6 opacity-0'
						    : 'translate-y-0 opacity-100'
				}`}
			>
				<div
					className={`rounded-[2.25rem] border border-zinc-200 bg-white p-6 shadow-sm transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] md:p-10 ${
						stage === 1
							? firstScreenEntered
								? 'scale-100'
								: 'scale-[0.97]'
							: stage === 2
							  ? secondScreenEntered
									? 'scale-100'
									: 'scale-[0.97]'
							  : stage === 3
							    ? thirdScreenEntered
										? 'scale-100'
										: 'scale-[0.97]'
							    : 'scale-100'
					}`}
				>
					<div
						className={`flex justify-center transition-opacity duration-500 ease-out md:pt-2 ${
							stage === 1
								? firstScreenEntered
									? 'opacity-100'
									: 'opacity-0'
								: stage === 2
								  ? secondScreenEntered
										? 'opacity-100'
										: 'opacity-0'
								  : stage === 3
								    ? thirdScreenEntered
											? 'opacity-100'
											: 'opacity-0'
								    : 'opacity-100'
						}`}
						style={{
							transitionDelay:
								stage === 1 || stage === 2 || stage === 3
									? '80ms'
									: '0ms',
						}}
					>
						<img
							src={PLUMAA_LOGO_URL}
							alt="Plumaa"
							className="h-14 w-auto object-contain md:h-16"
							draggable={false}
						/>
					</div>

					{stage === 1 && (
						<section
							className={`mx-auto mt-10 w-full max-w-4xl rounded-2xl transition-all duration-500 ease-out md:mt-12 ${
								firstScreenEntered
									? 'translate-y-0 opacity-100'
									: 'translate-y-4 opacity-0'
							}`}
							style={{ transitionDelay: '140ms' }}
						>
							<p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-orange/80">
								A jornada começa
							</p>
							<h1 className="mt-3 text-4xl font-black leading-[0.95] tracking-tight text-zinc-900 md:text-6xl">
								{displayName}, seja muito{' '}
								<span className="inline-flex items-center gap-2 text-brand-orange">
									bem-vindo(a).
									<Emoji unified="1f60a" size={44} />
								</span>
							</h1>
							<h2 className="mt-4 text-2xl font-black tracking-tight text-zinc-900">
								Que bom ter você aqui.
							</h2>
							<div className="mb-4 mt-2 h-1 w-14 rounded-full bg-brand-orange" />
							<p className="text-base leading-relaxed text-zinc-600 md:text-xl">
								A sua empresa trouxe a Plumaa. A Plumaa foi
								criada para tornar seu cuidado emocional mais
								simples, próximo e contínuo.
							</p>
							<p className="mt-4 text-sm leading-relaxed text-zinc-600 md:text-base">
								A partir de agora, você terá um ambiente pensado
								para acompanhar sua rotina com clareza e
								praticidade.
							</p>
							<p className="mt-2 text-sm leading-relaxed text-zinc-600 md:text-base">
								Nosso objetivo é tornar cada etapa mais leve e
								realmente útil para o seu dia a dia.
							</p>

							<div className="mt-6 min-h-[52px] md:min-h-[56px]">
								<div
									className={`transition-all duration-500 ease-out ${
										showContinueButton
											? 'pointer-events-auto translate-y-0 opacity-100'
											: 'pointer-events-none translate-y-2 opacity-0'
									}`}
								>
									<Button
										type="button"
										text="Continuar"
										onClick={() => setStage(2)}
										endIcon={
											<ArrowRight className="size-4 stroke-[2.5] text-white" />
										}
										className="ml-auto w-full justify-center rounded-xl bg-brand-orange py-2.5 text-sm font-bold text-white hover:bg-brand-orange/90 md:w-auto md:px-8 md:py-3 md:text-base"
									/>
								</div>
							</div>
						</section>
					)}

					{stage === 2 && (
						<section
							className={`mx-auto mt-10 w-full max-w-4xl rounded-2xl transition-all duration-500 ease-out md:mt-12 ${
								secondScreenEntered
									? 'translate-y-0 opacity-100'
									: 'translate-y-4 opacity-0'
							}`}
							style={{ transitionDelay: '140ms' }}
						>
							<p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-orange/80">
								Uma visão geral
							</p>
							<h2 className="mt-2 text-4xl font-black leading-[0.95] tracking-tight text-zinc-900 md:text-6xl">
								O que vem em{' '}
								<span className="inline-flex items-center gap-2 text-brand-orange">
									seguida?
									<Emoji unified="1f914" size={44} />
								</span>
							</h2>
							<h3 className="mt-6 text-2xl font-black tracking-tight text-zinc-900">
								Explore novas possibilidades.
							</h3>
							<p className="mt-4 text-base leading-relaxed text-zinc-600 md:text-[1.85rem] md:leading-relaxed">
								A Plumaa acompanha você além de uma única etapa,
								com recursos que apoiam seu bem-estar de forma
								contínua.
							</p>

							<div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
								<div className="flex items-start gap-3 rounded-2xl border border-zinc-200 bg-white p-4">
									<span className="inline-flex rounded-lg bg-zinc-50 p-2 text-brand-orange shadow-sm">
										<Info className="h-4 w-4" />
									</span>
									<div>
										<p className="text-sm font-bold text-zinc-900">
											Dúvida
										</p>
										<p className="mt-1 text-xs leading-relaxed text-zinc-600">
											Transformamos incertezas em clareza
											para apoiar sua jornada.
										</p>
									</div>
								</div>

								<div className="flex items-start gap-3 rounded-2xl border border-zinc-200 bg-white p-4">
									<span className="inline-flex rounded-lg bg-zinc-50 p-2 text-brand-orange shadow-sm">
										<CheckCircle2 className="h-4 w-4" />
									</span>
									<div>
										<p className="text-sm font-bold text-zinc-900">
											Resposta
										</p>
										<p className="mt-1 text-xs leading-relaxed text-zinc-600">
											Cada passo concluído abre novas
											possibilidades dentro da plataforma.
										</p>
									</div>
								</div>
							</div>

							<div className="mt-6 min-h-[52px] md:min-h-[56px]">
								<div
									className={`transition-all duration-500 ease-out ${
										showAdvanceButton
											? 'pointer-events-auto translate-y-0 opacity-100'
											: 'pointer-events-none translate-y-2 opacity-0'
									}`}
								>
									<Button
										type="button"
										text="Avançar"
										onClick={() => setStage(3)}
										endIcon={
											<ArrowRight className="size-4 stroke-[2.5] text-white" />
										}
										className="ml-auto w-full justify-center rounded-xl bg-brand-orange py-2.5 text-sm font-bold text-white hover:bg-brand-orange/90 md:w-auto md:px-8 md:py-3 md:text-base"
									/>
								</div>
							</div>
						</section>
					)}

					{stage === 3 && (
						<section
							className={`mx-auto mt-10 w-full max-w-4xl rounded-2xl transition-all duration-500 ease-out md:mt-12 ${
								thirdScreenEntered
									? 'translate-y-0 opacity-100'
									: 'translate-y-4 opacity-0'
							}`}
							style={{ transitionDelay: '140ms' }}
						>
							<p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-orange/80">
								Momento de avançar
							</p>
							<h2 className="mt-2 text-4xl font-black leading-[0.95] tracking-tight text-zinc-900 md:text-6xl">
								Vamos{' '}
								<span className="inline-flex items-center gap-2 text-brand-orange">
									começar?
									<Emoji unified="1f680" size={44} />
								</span>
							</h2>
							<h3 className="mt-6 text-2xl font-black tracking-tight text-zinc-900">
								Seu próximo passo é hoje.
							</h3>
							<p className="mt-4 text-base leading-relaxed text-zinc-600 md:text-[1.85rem] md:leading-relaxed">
								Você responderá seu formulário inicial para
								personalizar sua experiência desde o início.
							</p>
							<p className="mt-2 text-sm leading-relaxed text-zinc-600 md:text-base">
								Leva poucos minutos e prepara seu ambiente para
								você aproveitar todos os recursos da Plumaa.
							</p>

							<div className="mt-8 min-h-[52px] md:min-h-[56px]">
								<div
									className={`transition-all duration-500 ease-out ${
										showStartButton
											? 'pointer-events-auto translate-y-0 opacity-100'
											: 'pointer-events-none translate-y-2 opacity-0'
									}`}
								>
									<Button
										type="button"
										text="Vamos"
										onClick={handleStartForm}
										endIcon={
											<ArrowRight className="size-4 stroke-[2.5] text-white" />
										}
										className="ml-auto w-full justify-center rounded-xl bg-brand-orange py-3 text-sm font-bold text-white hover:bg-brand-orange/90 md:w-auto md:px-10 md:text-base"
									/>
								</div>
							</div>
						</section>
					)}
				</div>
			</div>
		</section>
	);
};

export { UserFirstAccess };
